import styled, { css, keyframes } from "styled-components";
import { motion } from "motion/react";

const crtOn = keyframes`
  0%   { transform: scale(1, 0.004); filter: brightness(8); }
  40%  { transform: scale(1, 0.004); filter: brightness(8); }
  70%  { transform: scale(1, 1.05); filter: brightness(2); }
  100% { transform: scale(1, 1); filter: brightness(1); }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const xpBlocks = keyframes`
  0% { transform: translateX(-40px); }
  100% { transform: translateX(170px); }
`;

const flicker = keyframes`
  0%, 100% { opacity: 0.12; }
  50% { opacity: 0.08; }
`;

const pixelFont = css`
  font-family: "VT323", "Courier New", monospace;
  & * {
    font-family: inherit;
  }
`;

export const BootOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200000;
  background: #000;
  overflow: hidden;
  user-select: none;
`;

/* Scanlines + vignette sit over every boot phase for the CRT feel */
export const Scanlines = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 0) 2px,
    rgba(0, 0, 0, 0.9) 3px
  );
  animation: ${flicker} 0.15s infinite;
  box-shadow: inset 0 0 160px rgba(0, 0, 0, 0.85);
`;

export const PhaseLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
`;

export const BiosScreen = styled.div`
  ${pixelFont};
  position: absolute;
  inset: 0;
  padding: 32px 40px;
  color: #c0c0c0;
  font-size: 22px;
  line-height: 1.15;
  animation: ${crtOn} 0.9s ease-out both;
  transform-origin: center;
  white-space: pre;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 20px 16px;
  }
`;

export const BiosHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 18px;
`;

export const BiosBadge = styled.div`
  border: 2px solid #3c9dff;
  color: #3c9dff;
  padding: 6px 12px;
  text-align: center;
  line-height: 1;
  font-size: 1.1em;
  text-shadow: 0 0 8px rgba(60, 157, 255, 0.8);

  @media (max-width: 768px) {
    display: none;
  }
`;

export const BiosLine = styled.div<{ $tone?: "ok" | "warn" | "accent" }>`
  min-height: 1.15em;
  color: ${(p) =>
    p.$tone === "ok"
      ? "#5cff5c"
      : p.$tone === "warn"
        ? "#ffd84a"
        : p.$tone === "accent"
          ? "#ffffff"
          : "inherit"};
  text-shadow: 0 0 6px currentColor;
`;

export const Cursor = styled.span`
  display: inline-block;
  width: 0.55em;
  height: 1em;
  background: currentColor;
  vertical-align: text-bottom;
  animation: ${blink} 1s step-end infinite;
`;

export const BiosFooter = styled.div`
  position: absolute;
  left: 40px;
  bottom: 28px;
  color: #c0c0c0;

  @media (max-width: 768px) {
    left: 16px;
  }
`;

export const SplashScreen = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  background: #000;
  color: #fff;
`;

export const SplashLogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

export const SplashLogo = styled.img`
  width: 84px;
  height: 84px;
  image-rendering: pixelated;
  filter: drop-shadow(0 0 18px rgba(80, 140, 255, 0.55));
`;

export const SplashTitle = styled.div`
  font-size: 54px;
  letter-spacing: -1px;
  line-height: 1;
  font-weight: 700;

  & sup {
    font-size: 0.45em;
    color: #ff7a1a;
    margin-left: 4px;
    vertical-align: super;
  }

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

export const SplashSubtitle = styled.div`
  font-size: 13px;
  color: #9fb4ff;
  letter-spacing: 4px;
  text-transform: uppercase;
  text-align: center;
`;

export const XPProgress = styled.div`
  width: 170px;
  height: 20px;
  border: 2px solid #b2b2b2;
  border-radius: 4px;
  padding: 2px;
  overflow: hidden;
  position: relative;
`;

export const XPProgressBlocks = styled.div`
  display: flex;
  gap: 2px;
  height: 100%;
  animation: ${xpBlocks} 1.6s linear infinite;

  & span {
    width: 9px;
    height: 100%;
    border-radius: 1px;
    background: linear-gradient(
      to bottom,
      #2838c7 0%,
      #3c9dff 45%,
      #9fd4ff 55%,
      #2838c7 100%
    );
  }
`;

export const SplashCopyright = styled.div`
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 11px;
  color: #6b6b6b;
`;

/* ---------- XP style log-in screen ---------- */

export const LoginScreen = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: 13% 1fr 13%;
  background: #5a7edc;
  color: #fff;
`;

export const LoginBar = styled.div<{ $position: "top" | "bottom" }>`
  background: #00309c;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  font-size: 13px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    ${(p) => (p.$position === "top" ? "bottom: 0;" : "top: 0;")}
    background: ${(p) =>
      p.$position === "top"
        ? "linear-gradient(90deg, #00309c 0%, #9fb4ff 50%, #00309c 100%)"
        : "linear-gradient(90deg, #00309c 0%, #f99736 50%, #00309c 100%)"};
  }

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

export const LoginMain = styled.div`
  display: grid;
  grid-template-columns: 1fr 2px 1fr;
  align-items: center;
  background: radial-gradient(
    ellipse at 15% 10%,
    #91b1ef 0%,
    #5a7edc 45%,
    #5a7edc 100%
  );

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 2px auto;
    align-content: center;
    gap: 24px;
  }
`;

export const LoginDivider = styled.div`
  height: 70%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0) 100%
  );

  @media (max-width: 768px) {
    height: 2px;
    width: 70%;
    justify-self: center;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

export const LoginBrand = styled.div`
  justify-self: end;
  padding-right: 48px;
  text-align: right;

  @media (max-width: 768px) {
    justify-self: center;
    text-align: center;
    padding: 0 16px;
  }
`;

export const LoginName = styled.div`
  ${pixelFont};
  font-size: clamp(56px, 9vw, 120px);
  line-height: 0.9;
  text-shadow:
    3px 3px 0 #00309c,
    0 0 30px rgba(255, 255, 255, 0.35);
`;

export const LoginTagline = styled.div`
  margin-top: 10px;
  font-size: 15px;
  opacity: 0.9;
`;

export const LoginHint = styled.div`
  margin-top: 28px;
  font-size: 14px;
  opacity: 0.85;
`;

export const LoginUsers = styled.div`
  justify-self: start;
  padding-left: 48px;

  @media (max-width: 768px) {
    justify-self: center;
    padding: 0 16px;
  }
`;

export const UserTile = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 24px 10px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover,
  &:focus-visible {
    background: linear-gradient(90deg, #00309c 0%, rgba(0, 48, 156, 0) 100%);
    outline: none;
  }

  &:hover img,
  &:focus-visible img {
    border-color: #ffcc00;
  }
`;

export const UserAvatar = styled.img`
  width: 64px;
  height: 64px;
  padding: 8px;
  border: 3px solid #fff;
  border-radius: 6px;
  background: linear-gradient(135deg, #f7d88b 0%, #e58f29 100%);
  image-rendering: pixelated;
  transition: border-color 0.2s;
`;

export const UserName = styled.div`
  font-size: 22px;
`;

export const UserSub = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
`;

export const WelcomeText = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: clamp(48px, 8vw, 96px);
  font-style: italic;
  font-weight: 700;
  text-shadow: 3px 3px 0 #00309c;
`;

export const SkipButton = styled.button`
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 10;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: rgba(255, 255, 255, 0.7);
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #fff;
    border-color: #fff;
  }
`;
